/*
  CraftyCuddle — endless auto-scrolling row (cc-autoscroll).
  Same idea as the homepage category circles: the row drifts slowly and
  loops forever, and anyone can still swipe, drag, wheel or use the arrows.

  - Copies of the first group are added until there is always content on
    screen (a short group never leaves a gap before the loop). They are
    only made once the row comes near the screen, so rows further down the
    page cost nothing until they're reached.
  - scrollLeft is kept inside the first copy, [0, one group width), and is
    quietly shifted by one group width when it runs past either end — every
    copy is identical, so the jump is invisible. The row starts on its first
    item (lined up with the heading) and still loops backwards.
  - The drift pauses on hover, touch, wheel and keyboard focus, and resumes
    once the visitor has let go / moved away.
  - It only animates while on screen and while the tab is visible, and not
    at all with prefers-reduced-motion.
*/
(function () {
  if (window.customElements.get('cc-autoscroll')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CcAutoscroll extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.viewport = this.querySelector('[data-cc-as-viewport]');
      this.track = this.querySelector('[data-cc-as-track]');
      this.group = this.querySelector('[data-cc-as-group]');
      if (!this.viewport || !this.track || !this.group || !this.group.children.length) return;
      this._ready = true;

      this.speed = parseFloat(this.dataset.speed) || 30; // px per second
      this.loop = 0;
      this.pos = 0;
      this.raf = null;
      this.lastTime = null;
      this.paused = false;
      this.hovering = false;
      this.pointerDown = false;
      this.inView = false;
      this.holdNormalizeUntil = 0;
      this.lastWidth = 0;
      this.built = false;

      this.tick = this.tick.bind(this);
      this.bindEvents(); // builds on first approach to the screen

      var self = this;
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { if (self.built) self.build(); });
      }
      /* lazy images can change a group's width once they load */
      window.addEventListener('load', function () { if (self.built) self.build(); }, { once: true });
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._ready = false;
    }

    /* ---------- copies + measurements ---------- */
    build() {
      var viewport = this.viewport;
      /* keep the current place in the loop across rebuilds (fonts/images
         finishing loading, resizes) instead of jumping back to the start */
      var offset = this.loop ? viewport.scrollLeft : 0;
      this.track.querySelectorAll('[data-cc-as-clone]').forEach(function (node) { node.remove(); });
      this.loop = this.group.getBoundingClientRect().width;
      this.lastWidth = viewport.clientWidth;
      if (!this.loop) return;

      /* enough copies that from anywhere in the first group there's still a
         full screen plus an arrow jump (0.8 of a screen) of content ahead —
         one extra copy for a long row, a few more for a short one */
      var needed = Math.max(2, Math.ceil(1 + (2 * viewport.clientWidth) / this.loop));
      for (var i = 1; i < needed; i++) {
        var copy = this.group.cloneNode(true);
        copy.removeAttribute('data-cc-as-group');
        copy.setAttribute('data-cc-as-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('style, link, script').forEach(function (node) { node.remove(); });
        copy.querySelectorAll('[id]').forEach(function (node) { node.removeAttribute('id'); });
        copy.querySelectorAll('[data-shopify-editor-block]').forEach(function (node) {
          node.removeAttribute('data-shopify-editor-block');
        });
        copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(function (node) {
          node.setAttribute('tabindex', '-1');
        });
        this.track.appendChild(copy);
      }

      offset = ((offset % this.loop) + this.loop) % this.loop;
      if (this.loop - offset < 1) offset = 0; // scrollLeft rounding, not a real offset
      /* 1px, not 0: at 0 a backwards swipe produces no scroll event to wrap on */
      this.pos = Math.max(offset, 1);
      viewport.scrollLeft = this.pos;
      this.built = true;
    }

    /* keep scrollLeft inside [0, loop) — every copy is identical */
    normalize() {
      if (!this.loop || performance.now() < this.holdNormalizeUntil) return;
      var s = this.viewport.scrollLeft;
      if (s >= this.loop) {
        s -= this.loop;
      } else if (s < 1) {
        s += this.loop;
      } else {
        return;
      }
      this.pos = s;
      this.viewport.scrollLeft = s;
    }

    /* ---------- animation ---------- */
    tick(now) {
      if (this.lastTime === null) this.lastTime = now;
      var dt = Math.min(now - this.lastTime, 100); // no leap after a stall
      this.lastTime = now;

      if (!this.paused && this.loop) {
        /* keep our own float position: scrollLeft may round to whole pixels,
           and a sub-pixel step per frame would otherwise never move */
        this.pos += (this.speed * dt) / 1000;
        if (this.pos >= this.loop) this.pos -= this.loop;
        this.viewport.scrollLeft = this.pos;
      }
      this.raf = window.requestAnimationFrame(this.tick);
    }

    start() {
      if (this.raf !== null || reduceMotion.matches || !this.inView || document.hidden) return;
      this.lastTime = null;
      this.pos = this.viewport.scrollLeft;
      this.raf = window.requestAnimationFrame(this.tick);
    }

    stop() {
      if (this.raf === null) return;
      window.cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    pause() {
      this.paused = true;
      window.clearTimeout(this.resumeTimer);
    }

    /* resume only once the visitor has really let go */
    scheduleResume(delay) {
      var self = this;
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(function () {
        if (self.hovering || self.pointerDown) return;
        self.pos = self.viewport.scrollLeft;
        self.paused = false;
      }, delay);
    }

    /* ---------- events ---------- */
    bindEvents() {
      var self = this;
      var viewport = this.viewport;

      /* mouse only: a tap on a phone also fires mouseenter, but never the
         matching mouseleave, which would leave the row paused for good */
      viewport.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse') return;
        self.hovering = true;
        self.pause();
      });
      viewport.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'mouse') return;
        self.hovering = false;
        self.scheduleResume(500);
      });

      ['pointerdown', 'touchstart'].forEach(function (type) {
        viewport.addEventListener(type, function () { self.pointerDown = true; self.pause(); }, { passive: true });
      });
      ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach(function (type) {
        viewport.addEventListener(type, function () { self.pointerDown = false; self.scheduleResume(1200); }, { passive: true });
      });

      var wheelIdle;
      viewport.addEventListener('wheel', function () {
        self.pause();
        window.clearTimeout(wheelIdle);
        wheelIdle = window.setTimeout(function () { self.scheduleResume(800); }, 250);
      }, { passive: true });

      this.addEventListener('focusin', function () { self.pause(); });
      this.addEventListener('focusout', function (event) {
        if (self.contains(event.relatedTarget)) return;
        self.scheduleResume(1200);
      });

      /* manual scrolling (swipe, drag, arrows) — wrap at the ends */
      viewport.addEventListener('scroll', function () {
        if (self.paused) self.normalize();
      }, { passive: true });

      var prev = this.querySelector('[data-cc-as-prev]');
      var next = this.querySelector('[data-cc-as-next]');
      function nudge(direction) {
        self.pause();
        var jump = viewport.clientWidth * 0.8;
        var s = viewport.scrollLeft;
        /* move to the matching spot in the next copy first, so the smooth
           scroll never runs into either end */
        if (direction < 0 && s - jump < 1) {
          s += self.loop;
        } else if (direction > 0 && s >= self.loop) {
          s -= self.loop;
        }
        viewport.scrollLeft = s;
        self.pos = s;
        self.holdNormalizeUntil = performance.now() + 700; // let the smooth scroll finish
        viewport.scrollBy({ left: direction * jump, behavior: 'smooth' });
        self.scheduleResume(2800);
      }
      if (prev) prev.addEventListener('click', function () { nudge(-1); });
      if (next) next.addEventListener('click', function () { nudge(1); });

      this._onResize = function () {
        window.clearTimeout(self.resizeTimer);
        self.resizeTimer = window.setTimeout(function () {
          /* phones fire resize when the address bar hides — only width matters */
          if (self.built && Math.abs(viewport.clientWidth - self.lastWidth) > 1) self.build();
        }, 200);
      };
      window.addEventListener('resize', this._onResize);

      this._onVisibility = function () {
        if (document.hidden) { self.stop(); } else { self.start(); }
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          self.inView = entries[0].isIntersecting;
          if (self.inView) {
            if (!self.built) self.build();
            self.start();
          } else {
            self.stop();
          }
        }, { rootMargin: '300px 0px' });
        this.observer.observe(this);
      } else {
        this.inView = true;
        this.build();
        this.start();
      }
    }
  }

  window.customElements.define('cc-autoscroll', CcAutoscroll);
})();
